import type { Dictionary } from '@autobid/strapi-integration/typescript/webapi/Dictionary'
import type { SearchFormSelectProps } from '../types/components/Select'
import { useFormItem } from '@autobid/nuxt-pinia-store/store/searchStore/useFormItem'
import { computed, ref } from 'vue'
import { VEHICLE_TYPE_KEY } from '../constants/VEHICLE_TYPE_KEY'
import { VEHICLE_TYPE_TO_CATEGORY_MAP } from '../constants/VEHICLE_TYPE_TO_CATEGORY_MAP'
import { checkIsDefaultSelectName } from '../utils/formkit/checkIsDefaultSelectName'

interface UseDictionarySelectParams {
  context: SearchFormSelectProps['context']
  item: Dictionary
}

export function useDictionarySelect({
  context,
  item,
}: UseDictionarySelectParams) {
  const isDefaultName = checkIsDefaultSelectName(context?.node?.name ?? '')
  const key = String(isDefaultName ? context.dictionary : context.node?.name)

  const route = useRoute()

  const value = useFormItem({
    key,
    dictionary: context.dictionary,
  })
  const query = ref('')
  const options = computed(() => {
    if (!item.options) {
      return []
    }

    const selectedType = Number(route.query[VEHICLE_TYPE_KEY])
    const isSelectedTypeCorrect = selectedType && [1, 2, 3, 4].includes(selectedType)

    if ((key.includes('category') || key.includes('brand')) && isSelectedTypeCorrect) {
      const mappedSelectedType
        = VEHICLE_TYPE_TO_CATEGORY_MAP[selectedType as keyof typeof VEHICLE_TYPE_TO_CATEGORY_MAP]
      return item.options.filter(item =>
        item.types?.includes(mappedSelectedType),
      )
    }

    return item.options
  })

  const filteredOptions = computed(() => {
    if (!options.value)
      return []

    return options.value
      ?.filter(item =>
        item.label.toLowerCase().includes(query.value.toLowerCase()),
      )
      .map((item) => {
        return {
          ...item,
          id: item.id.toString(),
        }
      })
  })

  return {
    options,
    value,
    filteredOptions,
    query,
  }
}
